import { assertSupabaseConfigured, supabase } from "../lib/supabase";
import type { ClientStatus } from "../types/database";
import type { Profile } from "../types/tenant";
import type {
  ClientFormInput,
  ClientInviteResult,
  ClientListItem,
  ClientOnboardingResponse,
  ClientOnboardingResponseInput,
  ClientProfile,
} from "../types/trainer";

const PROGRESS_PHOTO_BUCKET = "client-progress-photos";

async function attachAssignedTrainers(clients: ClientProfile[]) {
  const trainerIds = [
    ...new Set(
      clients
        .map((client) => client.assigned_trainer_user_id)
        .filter((value): value is string => Boolean(value)),
    ),
  ];

  if (!trainerIds.length) {
    return clients.map((client) => ({ ...client, assigned_trainer: null }));
  }

  const { data, error } = await supabase
    .from("profiles")
    .select("id,email,full_name")
    .in("id", trainerIds);

  if (error) throw error;

  const profileMap = new Map(
    ((data ?? []) as Pick<Profile, "id" | "email" | "full_name">[]).map(
      (profile) => [profile.id, profile],
    ),
  );

  return clients.map((client) => ({
    ...client,
    assigned_trainer: client.assigned_trainer_user_id
      ? profileMap.get(client.assigned_trainer_user_id) ?? null
      : null,
  }));
}

export const clientService = {
  async getCurrentClient(userId: string): Promise<ClientProfile | null> {
    assertSupabaseConfigured();

    const { data, error } = await supabase
      .from("clients")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return (data as ClientProfile | null) ?? null;
  },

  async listClients(input: {
    tenantId: string;
    search?: string;
    status?: ClientStatus | "all";
    limit?: number;
  }): Promise<ClientListItem[]> {
    assertSupabaseConfigured();

    let query = supabase
      .from("clients")
      .select("*")
      .eq("tenant_id", input.tenantId)
      .order("created_at", { ascending: false })
      .limit(input.limit ?? 100);

    if (input.status && input.status !== "all") {
      query = query.eq("status", input.status);
    }

    if (input.search?.trim()) {
      const search = input.search.trim();
      query = query.or(`full_name.ilike.%${search}%,email.ilike.%${search}%`);
    }

    const { data, error } = await query;
    if (error) throw error;

    return attachAssignedTrainers((data ?? []) as ClientProfile[]);
  },

  async getClient(tenantId: string, clientId: string): Promise<ClientListItem | null> {
    assertSupabaseConfigured();

    const { data, error } = await supabase
      .from("clients")
      .select("*")
      .eq("tenant_id", tenantId)
      .eq("id", clientId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    const [client] = await attachAssignedTrainers([data as ClientProfile]);
    return client;
  },

  async getOnboardingResponse(clientId: string): Promise<ClientOnboardingResponse | null> {
    assertSupabaseConfigured();

    const { data, error } = await (supabase as any)
      .from("client_onboarding_responses")
      .select("*")
      .eq("client_id", clientId)
      .maybeSingle();

    if (error) throw error;
    return (data as ClientOnboardingResponse | null) ?? null;
  },

  async saveOnboardingResponse(
    input: ClientOnboardingResponseInput,
  ): Promise<ClientOnboardingResponse> {
    assertSupabaseConfigured();

    const { data, error } = await (supabase as any)
      .from("client_onboarding_responses")
      .upsert(input, { onConflict: "client_id" })
      .select("*")
      .single();

    if (error) throw error;
    return data as ClientOnboardingResponse;
  },

  async submitOnboardingResponse(responseId: string) {
    assertSupabaseConfigured();

    const { error } = await (supabase as any).rpc("submit_client_onboarding_response", {
      p_response_id: responseId,
    });

    if (error) throw error;
  },

  async uploadOnboardingPhoto(input: {
    client: ClientProfile;
    kind: "front" | "side" | "back";
    file: File;
  }) {
    assertSupabaseConfigured();

    const extension = input.file.name.split(".").pop()?.toLowerCase() || "jpg";
    const safeExtension = ["jpg", "jpeg", "png", "webp"].includes(extension)
      ? extension
      : "jpg";
    const path = [
      "tenant",
      input.client.tenant_id,
      "client",
      input.client.id,
      "onboarding",
      `${input.kind}-${Date.now()}.${safeExtension}`,
    ].join("/");

    const { error } = await supabase.storage
      .from(PROGRESS_PHOTO_BUCKET)
      .upload(path, input.file, {
        cacheControl: "3600",
        contentType: input.file.type || undefined,
        upsert: false,
      });

    if (error) throw error;
    return path;
  },

  getInviteLink(inviteUrl: string) {
    if (/^https?:\/\//i.test(inviteUrl)) return inviteUrl;
    const normalizedPath = inviteUrl.startsWith("/") ? inviteUrl : `/${inviteUrl}`;
    return `${window.location.origin}${normalizedPath}`;
  },

  async inviteClient(tenantId: string, input: ClientFormInput): Promise<ClientInviteResult> {
    assertSupabaseConfigured();

    const { data, error } = await supabase.rpc("trainer_create_client_invite", {
      p_tenant_id: tenantId,
      p_client_full_name: input.fullName,
      p_client_email: input.email,
      p_optional_note: input.optionalNote || null,
    });

    if (error) throw error;
    return data?.[0] as ClientInviteResult;
  },
};
